import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../components/AppIcon';

const ToastContext = createContext(null);

const DEFAULT_DURATION = 4000;

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const idRef = useRef(0);

  const remove = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const show = useCallback(({ type = 'info', title, message, duration = DEFAULT_DURATION }) => {
    const id = ++idRef.current;
    setToasts((prev) => [...prev, { id, type, title, message }]);
    if (duration > 0) {
      setTimeout(() => remove(id), duration);
    }
    return id;
  }, [remove]);

  const success = useCallback((message, title = 'Sucesso') => show({ type: 'success', title, message }), [show]);
  const error = useCallback((message, title = 'Erro') => show({ type: 'error', title, message }), [show]);
  const warning = useCallback((message, title = 'Aviso') => show({ type: 'warning', title, message }), [show]);
  const info = useCallback((message, title = 'Info') => show({ type: 'info', title, message }), [show]);

  const value = useMemo(() => ({ show, success, error, warning, info, remove }), [show, success, error, warning, info, remove]);

  const ToastItem = ({ toast }) => {
    const variants = {
      success: { bg: 'bg-success', fg: 'text-success-foreground', icon: 'CheckCircle' },
      error: { bg: 'bg-error', fg: 'text-error-foreground', icon: 'AlertCircle' },
      warning: { bg: 'bg-warning', fg: 'text-warning-foreground', icon: 'AlertTriangle' },
      info: { bg: 'bg-primary', fg: 'text-primary-foreground', icon: 'Info' }
    };
    const v = variants[toast.type] || variants.info;
    return (
      <div className={`pointer-events-auto w-[320px] rounded-lg shadow-elevation-3 border border-border bg-popover text-popover-foreground overflow-hidden animate-fade-in`}> 
        <div className={`flex items-start gap-3 p-3`}>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${v.bg} ${v.fg}`}>
            <Icon name={v.icon} size={18} />
          </div>
          <div className="flex-1 min-w-0">
            {toast.title && <div className="text-sm font-semibold truncate">{toast.title}</div>}
            {toast.message && <div className="text-sm text-muted-foreground break-words">{toast.message}</div>}
          </div>
          <button
            type="button"
            className="text-muted-foreground hover:text-foreground"
            onClick={() => remove(toast.id)}
            aria-label="Fechar"
          >
            <Icon name="X" size={16} />
          </button>
        </div>
      </div>
    );
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      {createPortal(
        <div className="fixed bottom-4 right-4 z-[80] flex flex-col gap-3 pointer-events-none">
          {toasts.map((t) => (
            <ToastItem key={t.id} toast={t} />
          ))}
        </div>,
        typeof document !== 'undefined' ? document.body : undefined
      )}
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast deve ser usado dentro de ToastProvider');
  }
  return ctx;
};